/**
 * sheetDrag — the one drag-down-to-dismiss gesture every sheet shares (lane
 * sheet-swipe, 2026-09-10; Scott, 11:31: "Yes, every sheet, before tonight").
 *
 * `use:sheetDrag={close}` on a sheet's panel. A downward drag that starts on the
 * grabber or header, or on the body while its scroll region is at the top,
 * follows the finger (translateY). On release it dismisses past 30% of the
 * sheet's height or on a fast downward flick, and springs back otherwise. A drag
 * inside a scrolled body scrolls the body and never dismisses. Backdrop tap, Done
 * and Escape stay each sheet's own.
 *
 * Touch events, not pointer events: once a `touch-action: pan-y` region starts a
 * native scroll the browser cancels the pointer stream, and only a non-passive
 * touchmove's preventDefault can take the pan from the scroller (and from the
 * iOS rubber band at the top). The first movement past SLOP decides the whole
 * touch, before the browser starts a scroll of its own. Raw listeners on the
 * node: Svelte 5's on* attributes are delegated to the root (SwimSwiper, tapGuard).
 */

/** Released past this fraction of the sheet's height, the sheet goes. */
export const DISMISS_FRACTION = 0.3;
/** A downward flick at this speed (px per ms) dismisses a shorter drag... Read
 *  over the last VELOCITY_WINDOW_MS only, so a drag held still before release
 *  never counts. (1 px/ms missed a 100 px flick in 109 ms in the harness.) */
export const FLICK_VELOCITY = 0.5;
/** ...once it has moved at least this far. */
export const FLICK_MIN_DY = 24;
/** Movement (px) before the touch is decided: under the browser's own scroll start. */
export const SLOP = 4;
/** The flick speed is read over this much of the drag before the finger lifts. */
export const VELOCITY_WINDOW_MS = 100;

/** Touches that start on a field belong to the field. */
const NO_DRAG = "input, textarea, select, [contenteditable]";

export type DragDecision = "wait" | "claim" | "pass";

/** The first movement past SLOP decides the whole touch: down and at the top claims, anything else passes. */
export function decideDrag(dx: number, dy: number, atTop: boolean): DragDecision {
  if (Math.abs(dx) < SLOP && Math.abs(dy) < SLOP) return "wait";
  if (dy <= 0 || Math.abs(dx) > dy) return "pass";
  return atTop ? "claim" : "pass";
}

export interface DragSample {
  y: number;
  t: number;
}

/** Downward speed (px/ms) over the last VELOCITY_WINDOW_MS before `now`; 0 when the finger had stopped. */
export function releaseVelocity(samples: DragSample[], now: number): number {
  const recent = samples.filter((s) => now - s.t <= VELOCITY_WINDOW_MS);
  if (recent.length < 2) return 0;
  const first = recent[0];
  const last = recent[recent.length - 1];
  return (last.y - first.y) / Math.max(1, last.t - first.t);
}

export function shouldDismiss(offset: number, height: number, velocity: number): boolean {
  if (offset <= 0) return false;
  return offset >= height * DISMISS_FRACTION || (velocity >= FLICK_VELOCITY && offset >= FLICK_MIN_DY);
}

/** True when no scroll container from the touched element up to the sheet (inclusive) is scrolled down. */
export function atScrollTop(target: Element | null, sheet: Element): boolean {
  for (let el = target; el; el = el.parentElement) {
    if (el.scrollTop > 0 && el.scrollHeight > el.clientHeight + 1 && /auto|scroll/.test(getComputedStyle(el).overflowY)) {
      return false;
    }
    if (el === sheet) break;
  }
  return true;
}

function reducedMotion(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function sheetDrag(node: HTMLElement, onDismiss: () => void) {
  let dismiss = onDismiss;
  let state: "idle" | DragDecision = "idle";
  let startX = 0;
  let startY = 0;
  let offset = 0;
  let samples: DragSample[] = [];

  function springBack() {
    node.style.transition = reducedMotion() ? "none" : "transform var(--dur-card) var(--ease-swim)";
    node.style.transform = "";
  }

  function start(e: TouchEvent) {
    if (state === "claim") springBack();
    const target = e.target instanceof Element ? e.target : null;
    if (e.touches.length !== 1 || target?.closest(NO_DRAG)) {
      state = "idle";
      return;
    }
    startX = e.touches[0].clientX;
    startY = e.touches[0].clientY;
    offset = 0;
    samples = [];
    state = "wait";
  }

  function move(e: TouchEvent) {
    if (state !== "wait" && state !== "claim") return;
    const t = e.touches[0];
    if (!t) return;
    const dy = t.clientY - startY;
    if (state === "wait") {
      // A control inside the sheet that took the move for itself keeps it.
      state = e.defaultPrevented ? "pass" : decideDrag(t.clientX - startX, dy, atScrollTop(e.target as Element, node));
      if (state !== "claim") return;
      node.style.transition = "none";
    }
    if (e.cancelable) e.preventDefault();
    offset = Math.max(0, dy);
    node.style.transform = offset > 0 ? `translateY(${offset}px)` : "";
    samples.push({ y: t.clientY, t: performance.now() });
    if (samples.length > 20) samples.shift();
  }

  function end() {
    const claimed = state === "claim";
    state = "idle";
    if (!claimed) return;
    if (shouldDismiss(offset, node.offsetHeight, releaseVelocity(samples, performance.now()))) {
      dismiss();
      // The sheet unmounts on close; one still here a frame later goes back up.
      requestAnimationFrame(() => {
        if (node.isConnected) springBack();
      });
    } else {
      springBack();
    }
  }

  function cancel() {
    // The system took the touch back: never a dismiss.
    if (state === "claim") springBack();
    state = "idle";
  }

  node.addEventListener("touchstart", start, { passive: true });
  node.addEventListener("touchmove", move, { passive: false });
  node.addEventListener("touchend", end, { passive: true });
  node.addEventListener("touchcancel", cancel, { passive: true });

  return {
    update(next: () => void) {
      dismiss = next;
    },
    destroy() {
      node.removeEventListener("touchstart", start);
      node.removeEventListener("touchmove", move);
      node.removeEventListener("touchend", end);
      node.removeEventListener("touchcancel", cancel);
    },
  };
}
